import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { prefetchSubscription } from '@/hooks/useGlobalSubscription';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // Remove Lovable preview token from URL to prevent session override
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('__lovable_token')) {
      console.warn('[AUTH] Lovable preview token detected, removing to protect session...');
      urlParams.delete('__lovable_token');
      const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[AUTH] Auth state change:', event);
        
        // Pre-fetch subscription data on login (runs in background, non-blocking)
        if (event === 'SIGNED_IN' && session?.user) {
          console.log('[AUTH] Pre-fetching subscription data for user:', session.user.id);
          prefetchSubscription(queryClient, session.user.id);
        }
        
        // Warn on unexpected user switch
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          const currentEmail = user?.email;
          const newEmail = session?.user?.email;
          
          if (currentEmail && newEmail && currentEmail !== newEmail) {
            console.error('[AUTH] ⚠️ UNEXPECTED USER SWITCH DETECTED!', {
              previousEmail: currentEmail,
              newEmail: newEmail,
              event: event
            });
          }
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Get initial session and validate it
    const getInitialSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Error getting session:', sessionError);
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }
        
        if (session) {
          // Validate the session by checking if the user still exists
          const { error: userError } = await supabase.auth.getUser();
          
          if (userError) {
            console.warn('Session validation error:', userError.message);
            
            // Only logout for critical errors (JWT issues, invalid tokens)
            if (userError.message.includes('session_not_found') || 
                userError.message.includes('invalid_token') ||
                userError.message.includes('jwt')) {
              
              // Try to refresh the token before giving up
              console.log('Attempting token refresh...');
              const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
              
              if (!refreshError && refreshData.session) {
                console.log('Token refresh successful');
                setSession(refreshData.session);
                setUser(refreshData.session.user);
              } else {
                console.log('Token refresh failed, clearing session');
                await supabase.auth.signOut({ scope: 'local' });
                Object.keys(localStorage).forEach(key => {
                  if (key.startsWith('sb-oexgjmhrklbbkdfzzqjq-auth-token')) {
                    localStorage.removeItem(key);
                  }
                });
                setSession(null);
                setUser(null);
              }
            } else {
              // For network errors or temporary issues, keep the session
              console.log('Temporary error, keeping session');
              setSession(session);
              setUser(session.user);
            }
          } else {
            // Session is valid
            setSession(session);
            setUser(session.user);
          }
        } else {
          setSession(null);
          setUser(null);
        }
      } catch (error) {
        console.error('Unexpected error validating session:', error);
        // Keep session for unexpected errors (network issues, etc.)
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setSession(session);
          setUser(session.user);
        } else {
          setSession(null);
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata?: any) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: metadata
      }
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { error };
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      // Clear any remaining auth tokens from localStorage on any error
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-oexgjmhrklbbkdfzzqjq-auth-token')) {
          localStorage.removeItem(key);
        }
      });
      return { error };
    } catch (error) {
      // Force local cleanup even if signOut fails
      setSession(null);
      setUser(null);
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-oexgjmhrklbbkdfzzqjq-auth-token')) {
          localStorage.removeItem(key);
        }
      });
      return { error: error };
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    return { error };
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};